import { BoxProps } from '@mui/material';
import Box from '@mui/material/Box';
import clsx from 'clsx';

import { SpacingValue } from './_types';
import { resolveValue } from './_utils';
import { CssVarDefinitions } from '../../theme/types';
import { classNamesFor } from '../../utils/classNames';
import { insetVars, dividerVars, mergeStyles } from '../variables';

const classNamesFactory = classNamesFor('Inset');

const classNames = {
  root: classNamesFactory.root(),
};

type Padding = SpacingValue;

export type InsetProps = Omit<
  BoxProps,
  | 'px'
  | 'py'
  | 'pt'
  | 'pr'
  | 'pb'
  | 'pl'
  | 'paddingTop'
  | 'paddingRight'
  | 'paddingBottom'
  | 'paddingLeft'
> & {
  /**
   * Sets `paddingTop`
   * `true` - inherits from `insetVars.refs.pt`
   * `false` - no value
   * concrete number/string - sets to concrete value
   */
  paddingTop?: Padding;
  /**
   * Sets `paddingTop`
   * `true` - inherits from `insetVars.refs.pt`
   * `false` - no value
   * concrete number/string - sets to concrete value
   */
  paddingRight?: Padding;
  /**
   * Sets `paddingTop`
   * `true` - inherits from `insetVars.refs.pt`
   * `false` - no value
   * concrete number/string - sets to concrete value
   */
  paddingBottom?: Padding;
  /**
   * Sets `paddingTop`
   * `true` - inherits from `insetVars.refs.pt`
   * `false` - no value
   * concrete number/string - sets to concrete value
   */
  paddingLeft?: Padding;
  /** Alias for `paddingLeft` */
  pl?: Padding;
  /** Alias for `paddingRight` */
  pr?: Padding;
  /**
   * Sets the same value for `paddingLeft` and `paddingRight`
   * @default true
   */
  px?: Padding;
  /** Alias for `paddingTop` */
  pt?: Padding;
  /** Alias for `paddingBottom` */
  pb?: Padding;
  /** Sets the same value for `paddingTop` and `paddingBottom` */
  py?: Padding;
  /** Should show divider on the top */
  dividerTop?: boolean;
  /** Should show divider on the right */
  dividerRight?: boolean;
  /** Should show divider on the bottom */
  dividerBottom?: boolean;
  /** Should show divider on the left */
  dividerLeft?: boolean;
  /** CSS var definitions will be added to style attribute */
  cssVars?: CssVarDefinitions;
  /** `data-testid` attribute value */
  testId?: string;
};

/**
 * Box (from mui) with padding applied to all sides or individual sides selectively.
 * <br>Padding<br>
 * If the padding value is `true`, its value will be inherited from the ancestor, via CSS variables.<br>
 * If the padding value is `false`, it will be zero.
 * <br>Divider<br>
 * Optionally, it can also put divider on any side. Divider is a simple border.
 * Divider support is useful for cases, where presence of the divider is configurable via prop (like DialogFooter)
 * Without 'border as divider' support, setting a `divider={true}` on components like Dialog footer
 * would either result in different HTML structure, depending on presence of divider,
 * or require an extra wrapper that would be redundant in cases where no divider is needed.
 * @see insetVars in `libs/design-system/src/components-v2/variables.ts`
 */
export const Inset = ({
  children,
  px = true,
  py,
  pt,
  pr,
  pb,
  pl,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  dividerTop,
  dividerRight,
  dividerBottom,
  dividerLeft,
  cssVars,
  style,
  testId,
  className,
  ...rest
}: InsetProps) => {
  return (
    <Box
      className={clsx(classNames.root, className)}
      paddingTop={resolveValue({
        value: insetVars.refs.pt,
        directional: py,
        concrete: paddingTop ?? pt,
      })}
      paddingRight={resolveValue({
        value: insetVars.refs.pr,
        directional: px,
        concrete: paddingRight ?? pr,
      })}
      paddingBottom={resolveValue({
        value: insetVars.refs.pb,
        directional: py,
        concrete: paddingBottom ?? pb,
      })}
      paddingLeft={resolveValue({
        value: insetVars.refs.pl,
        directional: px,
        concrete: paddingLeft ?? pl,
      })}
      borderTop={dividerTop ? dividerVars.refs.color : undefined}
      borderRight={dividerRight ? dividerVars.refs.color : undefined}
      borderBottom={dividerBottom ? dividerVars.refs.color : undefined}
      borderLeft={dividerLeft ? dividerVars.refs.color : undefined}
      style={mergeStyles(style, cssVars)}
      data-testid={testId}
      {...rest}
    >
      {children}
    </Box>
  );
};

Inset.classNames = classNames;
